import { useEffect, useState } from "react";
import api from "@/lib/api";
import { STATUS_LABELS, ROLE_LABELS } from "@/lib/constants";
import { Building2, Users, Factory, Euro, TrendingUp, ShoppingCart, FileText, ScrollText } from "lucide-react";

const money = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [munis, setMunis] = useState([]);
  const [logs, setLogs] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/analytics").then((r) => setStats(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/municipalities").then((r) => setMunis(r.data));
    api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  const kpi = (label, value, Icon, color = "#000091") => (
    <div className="card-flat p-5">
      <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</span><Icon size={18} style={{ color }} /></div>
      <div className="text-2xl lg:text-3xl font-black font-display mt-2">{value}</div>
    </div>
  );

  return (
    <div className="max-w-7xl">
      <div className="mb-6"><div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Administration centrale</div><h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight">Pilotage de la plateforme</h1></div>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {kpi("MRR", money(stats.mrr), TrendingUp, "#059669")}
            {kpi("ARR", money(stats.arr), TrendingUp, "#059669")}
            {kpi("GMV", money(stats.gmv), Euro)}
            {kpi("Commissions", money(stats.commissions), Euro, "#7c3aed")}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {kpi("Collectivités", stats.municipalities, Building2)}
            {kpi("Commerçants", stats.merchants, Users)}
            {kpi("Fabricants", stats.manufacturers, Factory)}
            {kpi("Dossiers", stats.total, FileText)}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {kpi("Commandes", stats.orders_count, ShoppingCart)}
            {kpi("Devis émis", stats.quotes_count, FileText)}
            {kpi("Taux conformité", `${stats.compliance_rate}%`, TrendingUp, "#059669")}
            {kpi("Refusés", stats.rejected, FileText, "#E1000F")}
          </div>
        </>
      )}

      <div className="flex gap-2 mb-4">
        {[["overview", "Collectivités"], ["users", "Utilisateurs"], ["logs", "Journal d'audit"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`} className={`px-4 py-2 text-sm font-semibold border transition-colors ${tab === v ? "bg-[#000091] text-white border-[#000091]" : "border-border hover:border-[#000091]"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid sm:grid-cols-3 gap-4">
          {munis.map((m) => (
            <div key={m.id} className="card-flat p-5">
              <Building2 size={22} className="text-[#000091] mb-2" />
              <div className="font-semibold">{m.name}</div>
              <div className="text-sm text-slate-500">{m.region} · INSEE {m.code}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="card-flat divide-y divide-border">
          {users.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3">
              <div><div className="font-semibold">{u.name}</div><div className="text-sm text-slate-500">{u.email}</div></div>
              <span className="text-xs uppercase tracking-wide border border-border px-2 py-1">{ROLE_LABELS[u.role] || u.role}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "logs" && (
        <div className="card-flat divide-y divide-border">
          {logs.length === 0 && <div className="p-8 text-center text-slate-500">Aucune entrée.</div>}
          {logs.map((l) => (
            <div key={l.id} className="px-5 py-3 text-sm flex items-center gap-3">
              <ScrollText size={16} className="text-slate-400 shrink-0" />
              <span className="font-semibold">{l.user_name || "—"}</span>
              <span className="text-slate-500">{l.action} · {l.object_type}</span>
              {l.new_value?.status && <span className="text-slate-500">→ {STATUS_LABELS[l.new_value.status] || l.new_value.status}</span>}
              <span className="ml-auto text-xs text-slate-400 font-mono">{new Date(l.created_at).toLocaleString("fr-FR")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
